/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ORDERS PAGE — VERSION 5.2 DYNAMIC
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * LOGIQUE:
 *   1. Lire infoSource.allSettings.pages.ORDERS → pageSettings { sections, facets }
 *   2. Résolution — CASCADE STRICTE à 3 niveaux :
 *
 *      ┌─────────────────────────────────────────────────────────────────┐
 *      │  NIVEAU 1 — pageSettings.sections[]                             │
 *      │    sections[] non vide → extraire sectionIds triés + props.     │
 *      │    PRIORITAIRE. Si valide → STOP.                               │
 *      ├─────────────────────────────────────────────────────────────────┤
 *      │  NIVEAU 2 — pageSettings.facets[]                               │
 *      │    facets[] non vide → utiliser comme liste de sectionIds.      │
 *      │    Utilisé UNIQUEMENT si sections[] est vide/null.              │
 *      │    Si valide → STOP.                                            │
 *      ├─────────────────────────────────────────────────────────────────┤
 *      │  NIVEAU 3 — PAGES_CONFIG_.ORDERS (defaults pagesConfig.js)      │
 *      │    Utilisé UNIQUEMENT si sections[] ET facets[] sont vides.     │
 *      └─────────────────────────────────────────────────────────────────┘
 *
 *   CORE SECTIONS (toujours présentes) :
 *     ordersFullLayout | ordersModals
 *
 *   OPTIONAL (présentes par défaut, retirables) :
 *     userView | newProducts
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Layout from "../Layout/Layout";
import PendingCheckoutResume from '../pageInside/payments/PendingCheckoutResume';
import { useLocation } from "react-router-dom";
import {
    isAuthenticated,
    listOrders,
    getUserViewed,
} from "../auth";
import { getUserView, taxProductAreaByState } from "../auth/apiProduct";
import DynamicPageRenderer from "../pageInside/exeemple_page/DynamicPageRenderer";
import { getInfoSourceUpdates } from "../pageInside/exeemple_page/allFacets";
import useDataLoader from "../pageInside/exeemple_page/DataLoader";
import {
    PAGES_CONFIG_,
    SECTIONS_LIBRARY,
} from "../pageInside/exeemple_page/pagesConfig";
import { laguangeElakay } from "../pageInside/dataElakay";
import { elakayAutoText } from "../pageInside/dataElakay";


// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES MODULE
// ═══════════════════════════════════════════════════════════════════════════

const PAGE_ID = 'ORDERS';

/**
 * CORE_SECTIONS — toujours présentes, toujours enabled.
 * AnimationSettings peut changer order/props mais pas les supprimer.
 */
const CORE_SECTIONS = ['ordersGrid'];

// ═══════════════════════════════════════════════════════════════════════════
// HELPER
// ═══════════════════════════════════════════════════════════════════════════

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

// ═══════════════════════════════════════════════════════════════════════════
// NIVEAU 3 — defaults depuis PAGES_CONFIG_.ORDERS
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_FACETS = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];
    if (!config?.sections?.length) {
        return ['ordersGrid', 'userView', 'newProducts', 'shopCategoriesMatrix'];
    }
    return config.sections
        .filter(s => s.enabled !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(s => s.sectionId);
})();

const DEFAULT_CONFIG_OVERRIDES = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];
    if (!config?.sections?.length) return {};

    const result = {};
    config.sections.forEach(s => {
        if (!s.sectionId) return;
        const entry = {};
        const lib = SECTIONS_LIBRARY[s.sectionId];

        if (lib?.defaultProps || s.props) {
            entry.props = { ...(lib?.defaultProps || {}), ...(s.props || {}) };
        }
        if (s.overrides && Object.keys(s.overrides).length > 0) {
            entry.overrides = { ...s.overrides };
        }
        if (Object.keys(entry).length > 0) {
            result[s.sectionId] = entry;
        }
    });
    return result;
})();

// ═══════════════════════════════════════════════════════════════════════════
// _extractFromSettings — NIVEAUX 1 & 2
// ═══════════════════════════════════════════════════════════════════════════

const _extractFromSettings = (settings) => {
    if (!settings || typeof settings !== 'object') return null;

    const { sections, facets } = settings;

    // ── Priorité 1 : sections[] ─────────────────────────────────────────────
    if (Array.isArray(sections) && sections.length > 0) {

        const mergedMap = new Map();

        sections.forEach(s => {
            if (!s.sectionId) return;
            mergedMap.set(s.sectionId, {
                sectionId: s.sectionId,
                order:     s.order    ?? 0,
                enabled:   CORE_SECTIONS.includes(s.sectionId)
                               ? true
                               : s.enabled !== false,
                props:     s.props    || {},
                overrides: s.overrides || {},
            });
        });

        // Garantir la présence des CORE absents
        CORE_SECTIONS.forEach((coreId, idx) => {
            if (!mergedMap.has(coreId)) {
                mergedMap.set(coreId, {
                    sectionId: coreId,
                    order:     -(CORE_SECTIONS.length - idx),
                    enabled:   true,
                    props:     {},
                    overrides: {},
                });
            }
        });

        const sorted = [...mergedMap.values()]
            .sort((a, b) => a.order - b.order);

        const resolvedFacets = sorted
            .filter(s => s.enabled)
            .map(s => s.sectionId);

        const configOverrides = {};
        sorted.forEach(s => {
            if (!s.sectionId) return;
            const entry = {};
            if (s.props     && Object.keys(s.props).length > 0)     entry.props     = s.props;
            if (s.overrides && Object.keys(s.overrides).length > 0) entry.overrides = s.overrides;
            if (Object.keys(entry).length > 0) configOverrides[s.sectionId] = entry;
        });

        if (resolvedFacets.length > 0) {
            return { facets: resolvedFacets, configOverrides };
        }
    }

    // ── Priorité 2 : facets[] ───────────────────────────────────────────────
    if (Array.isArray(facets) && facets.length > 0) {
        const base = [...facets];
        CORE_SECTIONS.forEach(coreId => {
            if (!base.includes(coreId)) base.unshift(coreId);
        });
        return { facets: base, configOverrides: {} };
    }

    return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// resolvePageConfig — CASCADE STRICTE 3 niveaux
// ═══════════════════════════════════════════════════════════════════════════

const resolvePageConfig = (pageSettings) => {
    const result = _extractFromSettings(pageSettings);
    if (result) return result;

    return {
        facets:          DEFAULT_FACETS,
        configOverrides: DEFAULT_CONFIG_OVERRIDES,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT
// ═══════════════════════════════════════════════════════════════════════════

const Orders = (props) => {
    const { infoSource, PreLinK = "/c/orders" } = props;

    const query     = useQuery();
    const { token } = isAuthenticated();
    const readOnlyOrderView = query.get("readonly") === "1";

    // ────────────────────────────────────────────────────────────────────────
    // 1. ORDERS STATE
    // ────────────────────────────────────────────────────────────────────────

    const filorderDefault = {
        orderStatus: "none",
        orderDate:   "months",
        myd:         6,
    };

    const [limit,         setLimit]         = useState(3);
    const [skip,          setSkip]          = useState(0);
    const [sizeOrders,    setSizeOrders]    = useState(0);
    const [filtOrders,    setFiltOrders]    = useState(filorderDefault);
    const [orders,        setOrders]        = useState([]);
    const [trackingOrder, setTrackingOrder] = useState(null);
    const [loading,       setLoading]       = useState(true);
    const [pageImgSms,    setPageImgSms]    = useState(false);
    const [getOrderID,    setOrderID]       = useState({ oid: "", pid: "", opid: "" });
    const [graphicNote,   setGraphicNote]   = useState("");
    const [graphicNoteBs, setGraphicNoteBs] = useState([]);
    const [graphicImgBs,  setGraphicImgBs]  = useState([]);

    // ────────────────────────────────────────────────────────────────────────
    // 2. MODALS STATE
    // ────────────────────────────────────────────────────────────────────────

    const [showFilterOrder,  setShowFilterOrder]  = useState(false);
    const [showTrakingOrder, setShowTrakingOrder] = useState(false);
    const [showItermOrder,   setShowItermOrder]   = useState(false);
    const [showOrderNote,    setShowOrderNote]    = useState(false);

    const divCatProduct = useRef(null);

    // ────────────────────────────────────────────────────────────────────────
    // 3. CASCADE — résoudre config depuis AnimationSettings
    // ────────────────────────────────────────────────────────────────────────

    const pageSettings = infoSource?.allSettings?.pages?.[PAGE_ID];

    const { facets, configOverrides: baseConfigOverrides } = useMemo(
        () => resolvePageConfig(pageSettings),
        [
            JSON.stringify(pageSettings?.sections),
            JSON.stringify(pageSettings?.facets),
        ]
    );

    // ────────────────────────────────────────────────────────────────────────
    // 4. DATA LOADER — sections dynamiques (userView, newProducts, etc.)
    //    useDataLoader gère automatiquement les loaders selon loaderType
    // ────────────────────────────────────────────────────────────────────────

    const {
        pageData:      dynamicPageData,
        loading:       dynamicLoading,
        mergePageData: mergeDynamicData,
        loadPageData,
        reloadFacets,
    } = useDataLoader({
        facets,
        infoSource,
        token,
    });

    // ────────────────────────────────────────────────────────────────────────
    // 5. SYNC infoSource → dynamicPageData (sections loaderType NONE)
    // ────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        const updates = getInfoSourceUpdates(facets, infoSource);
        if (Object.keys(updates).length > 0) {
            mergeDynamicData(updates);
        }
    }, [infoSource, facets]);

    // ────────────────────────────────────────────────────────────────────────
    // 6. CHARGEMENT initial + reload si facets changent
    // ────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        loadPageData();
    }, [JSON.stringify(facets)]);

    // ────────────────────────────────────────────────────────────────────────
    // 7. HELPERS ORDERS
    // ────────────────────────────────────────────────────────────────────────

    const scrollToParagraph = useCallback(() => {
        divCatProduct.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    const newCartProd = useCallback(async (newCart) => {
        if (!newCart || newCart.length === 0) {
            setOrders([]);
            setLoading(false);
            return;
        }
        for (let i = 0; i < newCart.length; i++) {
            const additionalData = await taxProductAreaByState(
                newCart[i]?.prod_User_Address?.country,
                newCart[i]?.prod_User_Address?.postalCode
            );
            if (additionalData) {
                newCart[i].taxprodZone = additionalData.zoneTax;
            }
        }
        setOrders(newCart);
        setLoading(false);
    }, []);

    const getCategoriesLastOrder = useCallback((ar) => {
        if (!ar || ar.length === 0) return;
        const la = ar[ar.length - 1];
        if (la?.prod_filterProduct?.catID) {
            mergeDynamicData({ categoryID: la.prod_filterProduct.catID });
        }
        const ownerInfo = {
            address:    la?.prod_User_Address,
            prod_Owner: la?.prod_Owner,
        };
        mergeDynamicData({ infoOwner: ownerInfo });
    }, [mergeDynamicData]);

    // ────────────────────────────────────────────────────────────────────────
    // 8. LOADERS ORDERS
    // ────────────────────────────────────────────────────────────────────────

    const loadOrders = useCallback((tokenValue, limitValue, filters, skipValue = 0) => {
        setLoading(true);
        listOrders(tokenValue, skipValue, parseInt(limitValue, 10), "", filters).then(
            (data) => {
                if (data?.error) {
                    console.error('[Orders] listOrders error:', data.error);
                    setLoading(false);
                } else {
                    getCategoriesLastOrder(data.orders);
                    newCartProd(data.orders);
                    setSizeOrders(
                        data.totalCount?.[0]?.count || 0
                    );
                }
            }
        );
    }, [getCategoriesLastOrder, newCartProd]);

    // ────────────────────────────────────────────────────────────────────────
    // 9. PAGINATION ORDERS
    // ────────────────────────────────────────────────────────────────────────

    const loadMore = useCallback(() => {
        const toSkip = skip + parseInt(limit, 10);
        scrollToParagraph();
        listOrders(token, toSkip, limit, "", filtOrders).then((data) => {
            if (data?.error) {
                console.error('[Orders] loadMore error:', data.error);
            } else {
                getCategoriesLastOrder(data.orders);
                newCartProd(data.orders);
                setSizeOrders(data.totalCount?.[0]?.count || 0);
                setSkip(toSkip);
            }
        });
    }, [skip, limit, filtOrders, token, scrollToParagraph, getCategoriesLastOrder, newCartProd]);

    const loadMore0 = useCallback(() => {
        const toSkip = Math.max(0, skip - parseInt(limit, 10));
        scrollToParagraph();
        listOrders(token, toSkip, limit, "", filtOrders).then((data) => {
            if (data?.error) {
                console.error('[Orders] loadMore0 error:', data.error);
            } else {
                getCategoriesLastOrder(data.orders);
                newCartProd(data.orders);
                setSizeOrders(data.totalCount?.[0]?.count || 0);
                setSkip(toSkip);
            }
        });
    }, [skip, limit, filtOrders, token, scrollToParagraph, getCategoriesLastOrder, newCartProd]);

    const bntOderView = useCallback((oid, pid, opid) => {
        setShowItermOrder(true);
        setOrderID({ oid, pid, opid });
    }, []);

    // ────────────────────────────────────────────────────────────────────────
    // 10. EFFECTS
    // ────────────────────────────────────────────────────────────────────────

    // Charger orders quand token / limit / filtOrders changent
    useEffect(() => {
        loadOrders(token, limit, filtOrders, 0);
    }, [token, limit, filtOrders]);

    // Init query params
    useEffect(() => {
        if (query.get("oid")) {
            setShowItermOrder(true);
            setOrderID({
                oid:  query.get("oid"),
                pid:  query.get("pid")  || "",
                opid: query.get("opid") || "",
            });
        }
    }, []);

    // ────────────────────────────────────────────────────────────────────────
    // 11. pageData — fusionner orders state + dynamicPageData
    // ────────────────────────────────────────────────────────────────────────

    const pageData = useMemo(() => ({
        // Données dynamiques (userView, newProducts, etc.)
        ...dynamicPageData,
        // Données orders (toujours présentes)
        orders,
        modalsState: {
            showFilterOrder,
            setShowFilterOrder,
            showTrakingOrder,
            setShowTrakingOrder,
            showItermOrder,
            setShowItermOrder,
            showOrderNote,
        },
    }), [
        dynamicPageData,
        orders,
        showFilterOrder,
        showTrakingOrder,
        showItermOrder,
        showOrderNote,
    ]);

    // ────────────────────────────────────────────────────────────────────────
    // 12. configOverrides finaux
    //     Merge: baseConfigOverrides (AnimationSettings + PAGES_CONFIG_)
    //          + callbacks React (non-sérialisables)
    // ────────────────────────────────────────────────────────────────────────

    const configOverrides = useMemo(() => ({
        ...baseConfigOverrides,
        ordersFullLayout: {
            ...baseConfigOverrides.ordersFullLayout,
            props: {
                ...(baseConfigOverrides.ordersFullLayout?.props || {}),
                skip,
                limit,
                sizeOrders,
                setShowFilterOrder,
                setTrackingOrder,
                setShowTrakingOrder,
                bntOderView,
                loadMore,
                loadMore0,
            },
        },
        ordersModals: {
            ...baseConfigOverrides.ordersModals,
            props: {
                ...(baseConfigOverrides.ordersModals?.props || {}),
                token,
                filtOrders,
                limit,
                setLimit,
                setFiltOrders,
                trackingOrder,
                getOrderID,
                pageImgSms,
                setPageImgSms,
                graphicNote,
                setGraphicNote,
                graphicNoteBs,
                setGraphicNoteBs,
                graphicImgBs,
                setGraphicImgBs,
                setShowOrderNote,
                readOnlyOrderView,
            },
        },
        userView: {
            ...baseConfigOverrides.userView,
            props: {
                ...(baseConfigOverrides.userView?.props || {}),
                nameSliderKey: 'yourViewed',
                linkSlider:    '/user-viewed',
                CataID:        true,
            },
        },
        newProducts: {
            ...baseConfigOverrides.newProducts,
            props: {
                ...(baseConfigOverrides.newProducts?.props || {}),
                nameSliderKey: 'newProduct',
                linkSlider:    '/new-product',
            },
        },
    }), [
        baseConfigOverrides,
        skip, limit, sizeOrders,
        filtOrders, trackingOrder, getOrderID,
        pageImgSms, graphicNote, graphicNoteBs, graphicImgBs, readOnlyOrderView,
        loadMore, loadMore0, bntOderView,
    ]);

    // ────────────────────────────────────────────────────────────────────────
    // 13. STYLES
    // ────────────────────────────────────────────────────────────────────────

    const myStyles = useMemo(() => ({
        width:           '100%',
        backgroundColor: '#f0f0f0',
    }), []);

    const isLoading = loading || dynamicLoading;

    // ────────────────────────────────────────────────────────────────────────
    // 14. RENDER
    // ────────────────────────────────────────────────────────────────────────

    return (
        <Layout
            infoSource={infoSource}
            title={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).orders.t001}
            description={elakayAutoText("h_41682905073222", "Node React E-commerce App")}
            className="container-fluid"
            SliderPunShow={false}
            loading={query.get('pendingCheckout') === '1' ? false : isLoading}
            {...infoSource?.allSettings?.layout}
        >
            {query.get('pendingCheckout') === '1' ? <PendingCheckoutResume /> : <div ref={divCatProduct} style={infoSource.styleNav}>
                <DynamicPageRenderer
                    facets={facets}
                    pageId={PAGE_ID}
                    pageData={pageData}
                    infoSource={infoSource}
                    laguangeElakay={laguangeElakay}
                    homeStyles={myStyles}
                    reloadFacets={reloadFacets}
                    configOverrides={configOverrides}
                />
            </div>}
        </Layout>
    );
};

export default Orders;
